import * as React from 'react';
import { useDispatch } from 'react-redux';
import { InlineFormItem, SaveChangesResult } from '../ui/InlineFormItem';
import { CandidateLink } from './CandidateItem';
import { addBlankCandidateAndSetEditMode, addBlankCandidateSystemCandidateAndSetEditMode, deleteCandidate, removeInlineCandidateIfEmpty, saveBlankCandidate, saveCandidateName, saveCandidatePosition, saveCandidateSystemCandidate, savePartyCandidatePosition } from '../../features/candidate/candidatesSlice';
import { getTranslator } from '../../Translator';
import * as selectors from '../../selectors';
import { addModeSet, candidateInlineEdited } from '../../features/party/PartyScreenSlice';
import { useAppSelector } from '../../hooks';

type EditableCandidateItemProps = {
    candidateId: string;
    candidateSystem: boolean;
    canShowCandidacyApproved: boolean | null;
}

export const EditableCandidateItem = (props: EditableCandidateItemProps) => {
    const account = useAppSelector(state => state.clientState.Account);
    const candidates = useAppSelector(state => state.candidates);
    const parties = useAppSelector(state => state.parties);
    const elections = useAppSelector(state => state.elections);
    const language = useAppSelector(state => state.language);
    const token = useAppSelector(state => state.clientState.RequestVerificationToken);
    const t = getTranslator(language);
    const dispatch = useDispatch();
    const hasCandidacy = useAppSelector(state => state.clientState.HasCandidacy);
    const partyScreenState = useAppSelector(state => state.partyScreen);

    const candidate = candidates.filter(x => x.ClientSideId === props.candidateId)[0];
    const party = parties.filter(x => x.PartyId === candidate.PartyId)[0];
    const partyCandidates = candidates.filter(x => x.PartyId === candidate.PartyId)
    const electionCandidates = candidates.filter(x => x.ElectionId === candidate.ElectionId)
    const electionPartys = parties.filter(x => x.ElectionId === candidate.ElectionId)
    const isCurrentlyEditing = partyScreenState.inlineEditingCandidateId === candidate.ClientSideId;
    const errors = partyScreenState.validationErrorsForCandidate[props.candidateId]!;
    const isStructureEditable = selectors.canEditCandidateStructure(account, candidates, parties, elections, candidate.CandidateId);

    const [name, setName] = React.useState<string>(candidate.Name);
    const [userName, setUserName] = React.useState<string>(candidate.UserName);

    const onFinishEditing = () => {
        if (partyScreenState.addMode) {
            if (props.candidateSystem) {
                addBlankCandidateSystemCandidateAndSetEditMode(candidate.ElectionId, dispatch);
            } else {
                addBlankCandidateAndSetEditMode(candidate.PartyId, parties, dispatch);
            }
        }
    }

    const onSaveChanges = (): Promise<SaveChangesResult> => {

        if (partyScreenState.addMode) {
            if (props.candidateSystem) {
                return saveCandidateSystemCandidate(candidate, name, token, dispatch)
            } else {
                return saveBlankCandidate(candidate, name, userName, token, dispatch);
            }
        } else {
            return saveCandidateName(candidate, name, userName, token, dispatch)
        }
    }

    const onsavePositionChanges = (oldPosition: number, placeBefore: boolean): Promise<SaveChangesResult> => {
        if (props.candidateSystem) {
            const movedParty = electionPartys.filter(x => x.Position === oldPosition)[0];
            const targetCandidate = electionCandidates.filter(x => x.ClientSideId === candidate.ClientSideId)[0];
            const targetParty = electionPartys.filter(x => x.PartyId === targetCandidate.PartyId)[0];

            if (placeBefore) {
                return savePartyCandidatePosition(movedParty, targetParty.Position, token, dispatch);

            } else {
                const afterTargetPartys = electionPartys.filter(x => x.Position > targetParty.Position);
                if (afterTargetPartys && afterTargetPartys.length > 1) {
                    return savePartyCandidatePosition(movedParty, afterTargetPartys[0].Position, token, dispatch);
                } else {
                    return savePartyCandidatePosition(movedParty, -1, token, dispatch);
                }
            }
        }
        else {
            const movedCandidate = partyCandidates.filter(x => x.Position === oldPosition)[0];
            const targetCandidate = partyCandidates.filter(x => x.ClientSideId === candidate.ClientSideId)[0];

            if (placeBefore) {
                return saveCandidatePosition(movedCandidate, targetCandidate.Position, token, dispatch);

            } else {
                const afterTargetCandidates = partyCandidates.filter(x => x.Position > targetCandidate.Position);
                if (afterTargetCandidates && afterTargetCandidates.length > 1) {
                    return saveCandidatePosition(movedCandidate, afterTargetCandidates[0].Position, token, dispatch);
                } else {
                    return saveCandidatePosition(movedCandidate, -1, token, dispatch);
                }
            }
        }
    }

    const onStartEditing = () => {
        removeInlineCandidateIfEmpty(partyScreenState.inlineEditingCandidateId, candidates, dispatch);
        dispatch(addModeSet({ addMode: false }));
        dispatch(candidateInlineEdited({ candidateId: candidate.ClientSideId }));
    }

    return <InlineFormItem
        className="party__candidate"
        link={<CandidateLink candidate={candidate} showCandidacyApproved={props.canShowCandidacyApproved} t={t} />}
        draggable={true}
        position={props.candidateSystem ? party?.Position : candidate.Position}
        saveChanges={onSaveChanges}
        savePositionChanges={onsavePositionChanges}
        onFinishEditing={onFinishEditing}
        errors={errors}
        key={candidate.CandidateId}
        editState={isCurrentlyEditing}
        onStartEditing={onStartEditing}
        onCancelEditing={() => partyScreenState.addMode ? undefined : dispatch(candidateInlineEdited({ candidateId: null }))}
        options={<ul><li><a onClick={() => deleteCandidate(candidate, token, parties, elections, dispatch)}>{t[
            'Delete']}</a></li></ul>}
        enableEditButton={isStructureEditable}
        enablePopup={isStructureEditable}
        optionsTitle={t['Options']}>
        <input autoFocus
            value={name}
            placeholder={t['Name']}
            required
            onChange={(e) => setName(e.currentTarget.value)}
            maxLength={50} />
        {hasCandidacy && !props.candidateSystem && <input
            value={userName}
            placeholder={t['User name']}
            onChange={(e) => setUserName(e.currentTarget.value)}
            maxLength={50} />}
    </InlineFormItem>

}

